import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ComoHomeMosaicBlockConfig } from '@front-platform/content-builder';
import { comoHomeMosaicBlockResolveConfig } from '@front-platform/content-builder';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { Component, HostBinding, OnDestroy, OnInit, inject, input } from '@angular/core';
import type { ComoHomeMosaicBlockScenarioContent } from '@front-platform/content-builder';
import { COMO_HOME_MOSAIC_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';

import { ComoService } from '@core/services/como/como.service';
import { BasketStoreSelector } from '@common/selectors/basket.selector';

import { ComoAsset, ComoUser } from '@core/models/como.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { CartService } from '@core/services/cart/cart.service';
import { BasketService } from '@core/services/basket/basket.service';
import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
export interface ComoHomeMosaicBlockRuntimeConfig {
  'loyalty_points_title'?: string;
  'credit_points_title'?: string;
  'points_label'?: string;
}

export type ComoHomeMosaicBlockConfigInput = Partial<
  Omit<ComoHomeMosaicBlockConfig, keyof ComoHomeMosaicBlockRuntimeConfig> &
    ComoHomeMosaicBlockRuntimeConfig
>;
export type ComoHomeMosaicBlockResolvedConfig = Omit<
  ComoHomeMosaicBlockConfig,
  keyof ComoHomeMosaicBlockRuntimeConfig
> &
  Partial<ComoHomeMosaicBlockRuntimeConfig>;
export interface ComoHomeMosaicBlockContract extends ConfigComponentContract<
  ComoHomeMosaicBlockConfigInput,
  ComoHomeMosaicBlockResolvedConfig
> {}

@Component({
  selector: 'app-como-home-mosaic-block',
  templateUrl: './como-home-mosaic-block.component.html',
  standalone: false,
})
export class ComoHomeMosaicBlockComponent
  implements OnInit, OnDestroy, ComoHomeMosaicBlockContract
{
  readonly config = input<ComoHomeMosaicBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ComoHomeMosaicBlockConfig,
    ComoHomeMosaicBlockRuntimeConfig
  >(comoHomeMosaicBlockResolveConfig, () => ({ config: this.config() }));
  get loyaltyPointsTitle(): string {
    return this.resolvedConfig()['loyalty_points_title'] as unknown as string;
  }
  get creditPointsTitle(): string {
    return this.resolvedConfig()['credit_points_title'] as unknown as string;
  }
  get pointsLabel(): string {
    return this.resolvedConfig()['points_label'] as unknown as string;
  }
  get displayPoints(): boolean {
    return this.resolvedConfig()['display_points'] as unknown as boolean;
  }
  get pointsCardLink(): string | undefined {
    return this.resolvedConfig()['points_card_link'] as unknown as string | undefined;
  }
  get pointsBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['points_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get pointsTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['points_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get displayCredits(): boolean {
    return this.resolvedConfig()['display_credits'] as unknown as boolean;
  }
  get creditsCardLink(): string | undefined {
    return this.resolvedConfig()['credits_card_link'] as unknown as string | undefined;
  }
  get creditsBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['credits_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get creditsTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['credits_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get displayAssets(): boolean {
    return this.resolvedConfig()['display_assets'] as unknown as boolean;
  }
  get assetsHeading(): string {
    return this.resolvedConfig()['assets_heading'] as unknown as string;
  }
  get assetsSubheading(): string | undefined {
    return this.resolvedConfig()['assets_subheading'] as unknown as string | undefined;
  }

  @HostBinding('style.display')
  get display() {
    return this.comoUser ? 'block' : 'none';
  }

  public comoUser?: ComoUser | null;
  public basketId$ = this.basketSelector.selectId;

  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(
    COMO_HOME_MOSAIC_BLOCK_COMPONENT,
  );
  private readonly _destroyerRef = new Subject<boolean>();

  constructor(
    private readonly comoService: ComoService,
    private basketSelector: BasketStoreSelector,
    private readonly cartService: CartService,
    private readonly basketService: BasketService,
    private readonly orderDispatcher: OrderStoreDispatcher,
  ) {}

  ngOnInit(): void {
    if (this.cb2PreviewState.isActive()) {
      this.previewScenario$
        .pipe(
          takeUntil(this._destroyerRef),
          tap(scenario => {
            const content = previewScenarioData<ComoHomeMosaicBlockScenarioContent>(
              scenario,
              COMO_HOME_MOSAIC_BLOCK_COMPONENT,
            );
            this.comoUser = (content?.comoUser as ComoUser | null | undefined) ?? null;
          }),
        )
        .subscribe();
      return;
    }

    this.comoService.loadComoUser();
    this.comoService
      .getComoUser$()
      .pipe(
        takeUntil(this._destroyerRef),
        tap(comoUser => (this.comoUser = comoUser)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroyerRef.next(true);
    this._destroyerRef.complete();
  }

  onAssetAdd(asset: ComoAsset): void {
    this.comoService.confirmUsePointToAddAsset(asset);
  }

  onAssetRemove($event: ComoAsset): void {
    const coupon = { key: $event.key };
    this.cartService.removeCoupon(coupon);
    this.basketService
      .removeBasketCoupon$(coupon)
      .pipe(switchMap(() => this.orderDispatcher.checkCartIsValid$()))
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ComoHomeMosaicBlockComponent, { 'keyAliases': {} });
